import { api } from './api';
import type {
  ErrorLog,
  ErrorLogCreateRequest,
  ErrorLogSearchParams,
  ErrorLogSearchResponse,
  ErrorLogDashboardResponse,
} from '../types';

export const errorLogsService = {
  // Search error logs with filters
  search: async (params?: ErrorLogSearchParams): Promise<ErrorLogSearchResponse> => {
    const response = await api.get<ErrorLogSearchResponse>('/api/v1/error-logs/list', { params });
    return response.data;
  },

  // Create a new error log
  create: async (data: ErrorLogCreateRequest): Promise<ErrorLog> => {
    const response = await api.post<ErrorLog>('/api/v1/error-logs/create', data);
    return response.data;
  },

  // Export error logs as CSV
  exportCSV: async (params?: ErrorLogSearchParams): Promise<Blob> => {
    const response = await api.get('/api/v1/error-logs/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  // Get dashboard data
  getDashboardData: async (params?: {
    timeRange?: string;
    sourceType?: string;
    errorSeverity?: string;
    errorCode?: string;
    includeArchive?: boolean;
    page?: number;
    limit?: number;
    /** Narrow every figure to one project; omitted keeps every readable project merged. */
    appId?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<ErrorLogDashboardResponse> => {
    const response = await api.post<ErrorLogDashboardResponse>('/api/v1/error-logs/dashboard', params || {});
    return response.data;
  },

  // Parameter analysis — omit `parameter` to list the available parameter names for
  // an error code; pass it to get that parameter's top values with counts.
  getParameterAnalysis: async (params: {
    errorCode?: string;
    parameter?: string;
    timeRange?: string;
    fromDate?: string;
    toDate?: string;
    /** Narrow every figure to one project; omitted keeps every readable project merged. */
    appId?: string;
    includeArchive?: boolean;
    limit?: number;
  }): Promise<{
    data: {
      parameters?: { parameter: string; count: number }[];
      parameter?: string;
      errorCode?: string;
      totalOccurrences?: number;
      distinctValues?: number;
      values?: { value: string; count: number; affectedUsers: number; lastSeen: string | null; percentage: number }[];
    };
  }> => {
    const response = await api.post('/api/v1/error-logs/parameter-analysis', params);
    return response.data;
  },

  // Get user analytics (server-side aggregation)
  getUserAnalytics: async (actorUserId: string, includeArchive = false, appId?: string): Promise<any> => {
    const response = await api.post('/api/v1/error-logs/user-analytics', { actorUserId, includeArchive, ...(appId ? { appId } : {}) });
    return response.data;
  },

  // Search records with pagination
  searchRecords: async (params: {
    errorCode?: string;
    errorSeverity?: string;
    sourceType?: string;
    sourceName?: string;
    actorUserId?: string;
    environment?: string;
    endpoint?: string;
    q?: string;
    page?: number;
    pageSize?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    includeArchive?: boolean;
    /** Preset window — 1h | 6h | 24h | 1d | 7d | 30d. Ignored when fromDate is set. */
    timeRange?: string;
    /** ISO timestamp; server converts to IST before matching eventDateTime. */
    fromDate?: string;
    toDate?: string;
    /** Narrow to records carrying this `parameters` key (optionally this value). */
    paramName?: string;
    paramValue?: string;
    /** Narrow every figure to one project; omitted keeps every readable project merged. */
    appId?: string;
  }): Promise<any> => {
    const response = await api.post('/api/v1/error-logs/search', params);
    return response.data;
  },
};